import { EStatusCode } from 'src/app/core/helpers/error-codes';
import { getSentry } from 'src/app/core/helpers/sentry-plugin-wrapper';
import { environment } from 'src/environments/environment';
import { translate } from '../../features/translate/translate';
import { HttpErrorResponse } from '@angular/common/http';

interface SentryError extends Partial<HttpErrorResponse> {
  tags?: Record<string, unknown>;
}

export function logErrorInSentry(error: SentryError): void {
  const status = error?.status ?? error?.error?.status;

  if (
    status === EStatusCode.UNAUTHORIZED ||
    status === EStatusCode.FORBIDDEN ||
    status === EStatusCode.UPGRADE_REQUIRED
  ) {
    return;
  }

  if (getSentryEnabled()) {
    const level = status === EStatusCode.BAD_REQUEST ? 'warning' : 'error';

    getSentry().setTag('error_status', status ? `${status}` : 'other');
    getSentry().captureException(error?.message || error, {
      level,
      fingerprint: ['{{ default }}', status] as never,
    });

    if (Object.keys(error?.tags || []).length > 0) {
      Object.entries(error.tags).forEach(([key, value]) => {
        getSentry().setTag(key, value as string);
      });
    }
  }

  if (!environment.production) {
    console.groupCollapsed('Error for Sentry');
    console.log(error);
    console.groupEnd();
  }
}

type TSlowWarning = {
  message: string;
  tag?: string;
  view?: string;
  workspaceId?: string;
  extra?: Record<string, unknown>;
};

export function captureSlowWarning({
  message,
  tag,
  view,
  workspaceId,
  extra,
}: TSlowWarning): void {
  if (!getSentryEnabled()) {
    return;
  }

  getSentry().captureMessage(message, {
    level: 'warning',
    tags: tag ? { [tag]: 'true', view, workspaceId } : undefined,
    extra,
  });
}

export const generateErrorPrompt = (
  error: Partial<HttpErrorResponse>,
  defaultPrompt: string,
): string => {
  if (error.status === EStatusCode.UNAUTHORIZED) {
    return translate('prompt_request_logged_out');
  } else {
    return translate(defaultPrompt);
  }
};

export function getSentryEnabled(): boolean {
  return environment.production;
}
